import { Col, Empty, Row, Table, Tabs } from "antd";
import { showError } from "common/ToastNotifications";
import { JdbcTableList, LoadingOverlay } from "components";
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";

class SystemConnectionSnowflakeTables extends Component {
    constructor(props) {
        super(props);
        this.state = {
            busyMessage: "",
            fetchingTables: false,
            permissionDenied: false,
            selectedTableName: "",
        };
        this.schemaRef = React.createRef();
    }

    componentWillMount() {
        this.setState({ fetchingTables: true });
        this.props.getSnowflakeConnectionTables(this.props.connectionId);
    }

    componentDidUpdate(prevProps) {
        this.onGetSnowflakeConnectionTablesResultUpdate(prevProps);
        const prevPreview = prevProps.getSnowflakeTablePreviewResult;
        const nextPreview = this.props.getSnowflakeTablePreviewResult;
        if (prevPreview !== nextPreview) {
            this.setState({ fetchingTables: false });
            if (nextPreview && nextPreview.success) {
                const rows = this.props.snowflakeTablePreview;
                const columns = rows.length ? Object.keys(rows[0]) : [];
                const tableName = this.state.selectedTableName;
                this.props.onTableSelected({ data: rows, columns, tableName });
            } else if (nextPreview && !nextPreview.success) {
                showError(nextPreview.message || "Failed to load table preview.");
            }
        }

        const prevSchema = prevProps.getSnowflakeTableSchemaResult;
        const nextSchema = this.props.getSnowflakeTableSchemaResult;
        if (prevSchema !== nextSchema) {
            if (nextSchema && nextSchema.success && this.props.snowflakeTableSchema) {
                // Auto-scroll to schema section
                setTimeout(() => {
                    if (this.schemaRef.current) {
                        this.schemaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                }, 300);
            } else if (nextSchema && !nextSchema.success) {
                showError(nextSchema.message || "Failed to load table schema.");
            }
        }
    }

    onGetSnowflakeConnectionTablesResultUpdate = (prevProps) => {
        if (this.props.getSnowflakeConnectionTablesResult && this.props.getSnowflakeConnectionTablesResult !== prevProps.getSnowflakeConnectionTablesResult) {
            if (!this.props.getSnowflakeConnectionTablesResult.success) {
                if (this.props.getSnowflakeConnectionTablesResult.code === "PERMISSION_DENIED") {
                    this.setState({ permissionDenied: true });
                } else if (
                    this.props.getSnowflakeConnectionTablesResult.message &&
                    this.props.getSnowflakeConnectionTablesResult.message.includes("database and schema are required")
                ) {
                    showError("Snowflake database and schema are required to browse tables. Update the connection settings and try again.");
                } else {
                    showError(this.props.getSnowflakeConnectionTablesResult.message || "Could not get Snowflake connection tables at this moment.");
                }
            }
            this.setState({ fetchingTables: false });
        }
    };

    handleTableClick = (table) => {
        if (!table) return;
        // Extract name string from table object
        const tableNameStr = typeof table === "string" ? table : table.Name;
        // Clear previous data and load new table data
        this.props.onTableSelected({ data: [], columns: [], tableName: tableNameStr });
        this.setState({ fetchingTables: true, busyMessage: `Loading ${tableNameStr} preview and schema...`, selectedTableName: tableNameStr });
        this.props.getSnowflakeTablePreview(this.props.connectionId, tableNameStr, 10);
        this.props.getSnowflakeTableSchema(this.props.connectionId, tableNameStr);
    };

    renderSchemaTable = () => {
        const schema = this.props.snowflakeTableSchema;
        if (!schema || schema.length === 0) return <Empty description="No schema available" />;

        // Check if it's an array of objects (typical schema format)
        if (Array.isArray(schema) && schema.length > 0 && typeof schema[0] === "object") {
            // Get all unique keys from all objects
            const allKeys = new Set();
            schema.forEach((item) => {
                Object.keys(item).forEach((key) => allKeys.add(key));
            });

            const columns = Array.from(allKeys).map((key) => ({
                title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
                dataIndex: key,
                key: key,
                render: (text) => {
                    if (text === null || text === undefined) return "—";
                    if (typeof text === "object") return JSON.stringify(text);
                    return text.toString();
                },
            }));

            return (
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, background: "#fff", overflow: "hidden" }}>
                    <Table
                        columns={columns}
                        dataSource={schema.map((item, idx) => ({ ...item, key: idx }))}
                        size="small"
                        pagination={false}
                        scroll={{ x: "max-content", y: 420 }}
                    />
                </div>
            );
        }

        // Fallback for other data structures: display as JSON
        return (
            <div style={{ height: 480, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, background: "#fff" }}>
                <pre style={{ background: "#f6f6f6", padding: 16, borderRadius: 6, overflow: "auto", margin: 0 }}>{JSON.stringify(schema, null, 2)}</pre>
            </div>
        );
    };

    renderPreviewTable = () => {
        const preview = this.props.snowflakeTablePreview;
        if (!preview || preview.length === 0) return <Empty description="No preview data available" />;

        // Check if it's an array of objects (typical data format)
        if (Array.isArray(preview) && preview.length > 0 && typeof preview[0] === "object") {
            const allKeys = new Set();
            preview.forEach((item) => {
                Object.keys(item).forEach((key) => allKeys.add(key));
            });

            const columns = Array.from(allKeys).map((key) => ({
                title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
                dataIndex: key,
                key: key,
                render: (text) => {
                    if (text === null || text === undefined) return "—";
                    if (typeof text === "object") return JSON.stringify(text);
                    return text.toString();
                },
                width: 150,
            }));

            return (
                <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, background: "#fff", overflow: "hidden" }}>
                    <Table
                        columns={columns}
                        dataSource={preview.map((item, idx) => ({ ...item, key: idx }))}
                        size="small"
                        pagination={false}
                        scroll={{ x: "max-content", y: 420 }}
                    />
                </div>
            );
        }

        // Fallback for other data structures: display as JSON
        return (
            <div style={{ height: 480, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, background: "#fff" }}>
                <pre style={{ background: "#f6f6f6", padding: 16, borderRadius: 6, overflow: "auto", margin: 0 }}>{JSON.stringify(preview, null, 2)}</pre>
            </div>
        );
    };

    render() {
        const hasSchema = this.props.snowflakeTableSchema && this.props.snowflakeTableSchema.length > 0;
        const hasPreview = this.props.snowflakeTablePreview && this.props.snowflakeTablePreview.length > 0;

        return (
            <Row style={{ flexDirection: "column", flexGrow: 1, height: "100%" }}>
                <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <LoadingOverlay busy={this.state.fetchingTables} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                    <JdbcTableList tables={this.props.snowflakeConnectionTables} onTableSelected={this.handleTableClick}></JdbcTableList>
                    {(hasSchema || hasPreview) && (
                        <div ref={this.schemaRef} style={{ marginTop: 40, paddingBottom: 40 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Data for {this.state.selectedTableName}</h3>
                            <div style={{ minHeight: 520 }}>
                                <Tabs
                                    defaultActiveKey="schema"
                                    items={[
                                        {
                                            key: "schema",
                                            label: `Schema (${this.props.snowflakeTableSchema?.length || 0} columns)`,
                                            children: hasSchema ? this.renderSchemaTable() : <Empty description="No schema data" />,
                                        },
                                        {
                                            key: "preview",
                                            label: `Preview Data (${this.props.snowflakeTablePreview?.length || 0} rows)`,
                                            children: hasPreview ? this.renderPreviewTable() : <Empty description="No preview data" />,
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    )}
                </Col>
            </Row>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        getSnowflakeConnectionTablesResult: state.systemConnectionSnowflake.getSnowflakeConnectionTablesResult,
        snowflakeConnectionTables: state.systemConnectionSnowflake.snowflakeConnectionTables,
        getSnowflakeTablePreviewResult: state.systemConnectionSnowflake.getSnowflakeTablePreviewResult,
        snowflakeTablePreview: state.systemConnectionSnowflake.snowflakeTablePreview,
        snowflakeTableSchema: state.systemConnectionSnowflake.snowflakeTableSchema,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getSnowflakeConnectionTables: (connectionId) => dispatch(actions.getSnowflakeConnectionTablesRequest(connectionId)),
        getSnowflakeTablePreview: (connectionId, tableName, limit) => dispatch(actions.getSnowflakeTablePreviewRequest(connectionId, tableName, limit)),
        getSnowflakeTableSchema: (connectionId, tableName) => dispatch(actions.getSnowflakeTableSchemaRequest(connectionId, tableName)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SystemConnectionSnowflakeTables));
